/**
 * Overlay Manager Component
 * 
 * Manages rendering of all registered overlays with lazy loading support.
 */

import { createEffect, onMount, onCleanup, Show, createSignal, createMemo, For } from "solid-js";
import * as d3 from "d3";
import { overlayRegistry } from "./registry";
import { BaseOverlayProps } from "./types";
import { debug, warn as logWarn, error as logError } from "../../../../utils/console";

export interface OverlayManagerProps {
  map: any;
  mapContainer: HTMLElement | null;
  data: BaseOverlayProps['data'];
  liveMode?: boolean;
  width: number;
  height: number;
  /** Optional external enabled state getters/setters - keyed by overlay key */
  enabledStates?: Record<string, { get: () => boolean; set: (v: boolean) => void }>;
  /** When provided (time-window playback), use for overlays so they end at boat position */
  effectivePlaybackTime?: Date | null;
  /** Samples per second for playback step (e.g. 1 = 1Hz). Used for bad air offset. */
  samplingFrequency?: number;
  /** Averaged per-boat wind (moving boats only) for the wind-field overlay. Absent/empty = mark-only field. */
  boatWindSamples?: BaseOverlayProps['boatWindSamples'];
  /** Time-tagged boat wind trail for the wind field's advected interpolation. */
  boatWindHistory?: BaseOverlayProps['boatWindHistory'];
}

export default function OverlayManager(props: OverlayManagerProps) {
  let svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any> | null = null;
  let overlayGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any> | null = null;
  const loadedComponents = new Map<string, any>();
  // Bumped when the overlay SVG becomes available, so persistent overlay components pick it up reactively.
  const [svgReady, setSvgReady] = createSignal(0);

  /**
   * Initialize SVG overlay layer
   */
  const initSVG = () => {
    if (!props.mapContainer || svg) {
      return;
    }

    // Try to find canvas container first
    let container = props.mapContainer.querySelector(".mapboxgl-canvas-container") as HTMLElement;
    
    // Fallback to .map container if canvas container not found
    if (!container) {
      container = props.mapContainer.querySelector(".map") as HTMLElement || props.mapContainer;
    }

    if (!container) {
      return;
    }

    // Check if overlay SVG already exists
    const existingSvg = d3.select(container).select<SVGSVGElement>("svg.map-overlays");
    
    if (!existingSvg.empty()) {
      svg = existingSvg;
      overlayGroup = svg.select<SVGGElement>("g.overlays");
      if (!overlayGroup || overlayGroup.empty()) {
        overlayGroup = svg.append<SVGGElement>("g").attr("class", "overlays");
      }
      setSvgReady((v) => v + 1);
      return;
    }

    // Create new SVG overlay
    const mapRect = container.getBoundingClientRect();
    const width = mapRect.width || props.width;
    const height = mapRect.height || props.height;

    svg = d3
      .select(container)
      .append<SVGSVGElement>("svg")
      .attr("class", "map-overlays")
      .attr("width", width)
      .attr("height", height)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("width", "100%")
      .style("height", "100%")
      .style("z-index", "40") // Below race course (marks & boundaries, z-index 50) so they stay on top
      .style("pointer-events", "none");

    overlayGroup = svg.append<SVGGElement>("g").attr("class", "overlays");
    setSvgReady((v) => v + 1);

    debug("OverlayManager: SVG initialized", { width, height });
  };

  /**
   * Update SVG dimensions
   */
  const updateDimensions = () => {
    if (!svg || !props.mapContainer) return;

    let container = props.mapContainer.querySelector(".mapboxgl-canvas-container") as HTMLElement | null;
    if (!container) {
      container = props.mapContainer.querySelector(".map") as HTMLElement | null;
    }
    if (!container) {
      container = props.mapContainer;
    }
    if (!container) return;

    const width = container.offsetWidth || props.width;
    const height = container.offsetHeight || props.height;

    svg
      .attr("width", width)
      .attr("height", height);
  };

  /**
   * Load and render an overlay
   */
  const renderOverlay = async (key: string, enabled: boolean) => {
    const overlay = overlayRegistry.get(key);
    if (!overlay) {
      return;
    }

    // Remove overlay if disabled
    if (!enabled) {
      if (overlayGroup) {
        overlayGroup.select(`.overlay-${key}`).remove();
      }
      return;
    }

    // Load component if not already loaded
    if (!overlay.loaded) {
      debug(`OverlayManager: Loading overlay: ${key}`);
      const component = await overlayRegistry.load(key);
      if (!component) {
        logError(`OverlayManager: Failed to load overlay: ${key}`);
        return;
      }
    }

    // Get the component
    const component = overlay.component;
    if (!component) {
      return;
    }

    // NOTE: mounting deliberately does NOT wait for the overlay SVG group.
    //
    // It used to return here when overlayGroup was still null (initSVG runs on a 500ms timer) WITHOUT recording
    // the overlay, so nothing retried it: an overlay enabled from persisted settings could stay unmounted until
    // some unrelated state change happened to re-run renderAllOverlays — e.g. toggling a different overlay. That
    // is what made "Mark Wind is on but nothing draws until I click Boat Wind" happen.
    //
    // Waiting was never necessary: `svg` is passed to every overlay REACTIVELY via svgForProps(), so an overlay
    // that needs it (bad air) simply renders nothing until it arrives and then draws, while one that does not
    // (mark wind, which owns a canvas and native layers) starts immediately.

    // Register the component ONCE for persistent JSX rendering. enabled/data/time/svg are supplied reactively
    // by the <For> render (see loadedEntries), so we bump the version only when NEWLY loading — re-enabling an
    // already-loaded overlay must not remount it (or all overlays), just flip its reactive `enabled` prop.
    if (!loadedComponents.has(key)) {
      loadedComponents.set(key, { component });
      setLoadedVersion((v) => v + 1);
    }
  };

  /**
   * Render all enabled overlays
   */
  const renderAllOverlays = async () => {
    // Only the map is required — see renderOverlay: overlays receive `svg` reactively, so gating the whole sweep
    // on the SVG group is what previously stranded an overlay that was enabled before initSVG's timer fired.
    if (!props.map) {
      return;
    }

    const overlays = overlayRegistry.getAll();
    
    for (const overlay of overlays) {
      // Check if external enabled state is provided, otherwise use registry
      let enabled = overlay.enabled;
      if (props.enabledStates && props.enabledStates[overlay.key]) {
        enabled = props.enabledStates[overlay.key].get();
        // Sync with registry
        overlayRegistry.setEnabled(overlay.key, enabled);
      }
      
      await renderOverlay(overlay.key, enabled);
    }
  };

  // Sync external enabled states with registry and update local state
  createEffect(() => {
    if (props.enabledStates) {
      const newStates: Record<string, boolean> = {};
      Object.keys(props.enabledStates).forEach(key => {
        const state = props.enabledStates![key];
        if (overlayRegistry.has(key)) {
          const enabled = state.get();
          overlayRegistry.setEnabled(key, enabled);
          newStates[key] = enabled;
          
          // If enabling an overlay for the first time, ensure it gets loaded and rendered
          if (enabled && !loadedComponents.has(key)) {
            // Component needs to be loaded
            renderOverlay(key, enabled);
          }
        }
      });
      // Update all registered overlays
      overlayRegistry.getAll().forEach(overlay => {
        if (!(overlay.key in newStates)) {
          newStates[overlay.key] = overlay.enabled;
        }
      });
      setOverlayStates(newStates);
      // Render whenever the states change. NOT gated on the SVG: overlays get `svg` reactively, and gating here
      // is what left an overlay enabled at mount unmounted until an unrelated toggle happened to re-run this.
      renderAllOverlays();
    } else {
      // Fallback to registry states
      const states: Record<string, boolean> = {};
      overlayRegistry.getAll().forEach(overlay => {
        states[overlay.key] = overlay.enabled;
      });
      setOverlayStates(states);
    }
  });

  // Initialize SVG on mount
  onMount(() => {
    // Wait a bit for map to be fully initialized
    setTimeout(() => {
      initSVG();
      updateDimensions();
      // Sweep again once the SVG exists, for overlays that were enabled from persisted settings.
      setTimeout(() => renderAllOverlays(), 100);
    }, 500);

    // Handle map resize
    if (props.map) {
      props.map.on("resize", updateDimensions);
      props.map.on("moveend", () => {
        // Small delay to ensure dimensions are updated
        setTimeout(updateDimensions, 100);
      });
    }
  });

  // Watch for SVG initialization and trigger overlay rendering
  // Also update loadedComponents props when SVG becomes available
  createEffect(() => {
    // When SVG becomes available and we have enabled overlays, update props and render
    if (svg && svg.node() && svg.node()?.parentNode && overlayGroup && props.enabledStates) {
      // Update props in loadedComponents to include the now-available SVG
      loadedComponents.forEach((entry, key) => {
        const state = props.enabledStates![key];
        const isEnabled = state && state.get && state.get();
        if (isEnabled) {
          // Update props with current SVG
          entry.props.svg = svg;
          entry.props.map = props.map;
          entry.props.mapContainer = props.mapContainer;
          entry.props.data = props.data;
          entry.props.liveMode = props.liveMode || false;
          entry.props.enabled = isEnabled;
          entry.props.width = props.width;
          entry.props.height = props.height;
        }
      });
      
      // Check if any overlay is enabled
      const hasEnabledOverlay = Object.keys(props.enabledStates).some(key => {
        const state = props.enabledStates![key];
        return state && state.get && state.get();
      });
      
      if (hasEnabledOverlay) {
        // Trigger re-render by updating overlayStates
        const states = overlayStates();
        setOverlayStates({ ...states });
      }
    }
  });

  // Update overlays when data changes
  createEffect(() => {
    if (props.data.length > 0 && props.map) {
      renderAllOverlays();
    }
  });

  // Track overlay enabled states to trigger re-renders
  const [overlayStates, setOverlayStates] = createSignal<Record<string, boolean>>({});
  // Bumped whenever loadedComponents changes. loadedComponents is a plain Map (non-reactive), so without this
  // the activeOverlays memo never recomputes after initSVG runs late and renderAllOverlays finally populates it
  // (that was the "overlayGroup not initialized, queuing" → overlay never mounts bug, e.g. bad-air).
  const [loadedVersion, setLoadedVersion] = createSignal(0);
  
  // Stable list of LOADED overlay components. Recomputes ONLY when a component is (lazily) loaded — NOT on
  // data/time changes — so overlays mount once and stay mounted (no per-frame remount churn). Show/hide is a
  // reactive `enabled` prop each overlay honors, not add/remove from the tree. This is what keeps a disabled
  // overlay fully idle (no polling / no worker pool churn) and an enabled one from respawning every tick.
  const loadedEntries = createMemo(() => {
    void loadedVersion();
    const out: Array<{ key: string; component: any }> = [];
    loadedComponents.forEach((entry, key) => {
      if (entry?.component) out.push({ key, component: entry.component });
    });
    return out;
  });

  /** Overlay SVG if it's attached (reactive via svgReady), else null. */
  const svgForProps = () => {
    void svgReady();
    return svg && svg.node() && svg.node()?.parentNode ? svg : null;
  };

  /** Reactive enabled state for an overlay key (external enabledStates → overlayStates → registry default). */
  const isOverlayEnabled = (key: string): boolean => {
    const states = overlayStates();
    return states[key] ?? overlayRegistry.get(key)?.enabled ?? false;
  };

  // Update SVG dimensions when props change
  createEffect(() => {
    updateDimensions();
  });

  // Cleanup
  onCleanup(() => {
    if (props.map) {
      props.map.off("resize", updateDimensions);
      props.map.off("moveend", updateDimensions);
    }
    
    if (svg) {
      svg.remove();
      svg = null;
    }
    
    loadedComponents.clear();
  });

  // Render loaded overlays persistently (mounted once via keyed <For>). Props are reactive expressions, so
  // enabled/data/time/svg update in place — the component never remounts on a data or playback tick, and a
  // disabled overlay stays mounted but idle+hidden (it honors enabled=false). This replaces the old
  // `activeOverlays().map()` which recreated every overlay on each recompute (per-frame remount churn).
  return (
    <For each={loadedEntries()}>
      {(entry) => {
        const Component = entry.component;
        return (
          <Component
            map={props.map}
            mapContainer={props.mapContainer}
            svg={svgForProps()}
            data={props.data}
            liveMode={props.liveMode || false}
            enabled={isOverlayEnabled(entry.key)}
            width={props.width}
            height={props.height}
            effectivePlaybackTime={props.effectivePlaybackTime ?? undefined}
            samplingFrequency={props.samplingFrequency ?? undefined}
            boatWindSamples={props.boatWindSamples ?? undefined}
            boatWindHistory={props.boatWindHistory ?? undefined}
          />
        );
      }}
    </For>
  );
}

